/* ORJN smart search — query rewriting (vocabulary layer).
   Runs on the search input + results. Expands abbreviations/nicknames, splits
   compound brand/model words (airforce->air force), and strips filler words,
   BEFORE the query reaches Shopify's native search. No redirects, no product
   changes — only rewrites the text query. Falls back to the raw query when
   nothing applies (never worse than native).

   IMPORTANT: native search ANDs terms, so we must not over-expand. We rewrite the
   query string conservatively and keep it on /search. */
(function () {
  'use strict';

  // --- Catalog-grounded vocabulary (auto-derived; safe to extend) ---

  // Multi-word model phrases users often type without spaces -> spaced form.
  // The bulk of this list is DATA-VALIDATED: each no-space form was tested against
  // the live store and only kept when the spaced rewrite returned MORE results.
  var COMPOUNDS = [
    // --- validated against live store (raw nospace -> spaced returns more) ---
    'adizero evo', 'adizero evo sl', 'air force', 'air force 1',
    'air jordan', 'air jordan 1', 'air max', 'air max 90',
    'air max 95', 'air max dn8', 'air zoom', 'air zoom pegasus',
    'aj 1 low', 'aj 1 mid', 'balance 1906r', 'balance 740',
    'balance 9060', 'campus 00s', 'cloudmonster void', 'copa pure',
    'dunk low', 'dunk low retro', 'evo sl', 'f50 elite',
    'f50 sparkfusion', 'field general', 'force 1', 'force 1 07',
    'force 1 low', 'future 9', 'gazelle bold', 'gazelle indoor',
    'handball spezial', 'ld 1000', 'legend 10', 'mafate speed',
    'max 270', 'max 90', 'max 95', 'max dn',
    'max dn8', 'max moto', 'max plus', 'mercurial superfly',
    'mercurial superfly 10', 'mercurial vapor', 'mercurial vapor 16', 'new balance',
    'new balance 1906r', 'new balance 740', 'new balance 9060', 'on cloud',
    'on cloud 6', 'on cloudmonster void', 'p 6000', 'pegasus 41',
    'pegasus premium', 'phantom 6 academy', 'predator elite', 'pure iv',
    'shadow 7', 'spizike low', 'structure 26', 'tiempo legend',
    'tiempo legend 10', 'tiempo maestro', 'ultimate fg', 'ultra 6',
    'vomero plus', 'wade 11', 'wade 808', 'wade 808 5',
    'wade all', 'wade shadow', 'wade shadow 7', 'zoom pegasus',
    'zoom pegasus 41', 'zoom vomero', 'zoom vomero 5',
    // --- additional known model lines (curated, safe) ---
    'samba og', 'superstar ii', 'sl 72', 'way of wade', 'all city',
    'clifton 10', 'bondi 9', 'rincon 4', 'air max plus', 'air max 270',
    'zoom fly', 'phantom 6', 'predator league', 'f50 league', 'jordan 1',
    'jordan retro', 'metcon 10', 'soccer shoes', 'indoor soccer'
  ];

  /* Nicknames / abbreviations -> canonical term(s).
     IMPORTANT: only include terms where the rewrite is verified BETTER than the
     raw query. We deliberately DO NOT rewrite ones that already return good
     results (e.g. "kd"=14 real results, "aj"=59) or ambiguous 2-letter codes
     (af/am/ub) that match real SKUs — rewriting those would downgrade search.
     We DO rewrite: compounds (no-space), zero-result nicknames, and known
     garbage-matchers (e.g. "wow" returned unrelated Jordans, "way of wade" is
     correct). */
  var ALIASES = {
    // ---- no-space compounds (raw usually 0) -> spaced ----
    'airforce': 'air force', 'airforce1': 'air force 1', 'airforceone': 'air force 1',
    'airmax': 'air max', 'airmax90': 'air max 90', 'airmax95': 'air max 95',
    'airmax270': 'air max 270', 'airmaxplus': 'air max plus', 'airmaxdn': 'air max dn',
    'airjordan': 'air jordan', 'airjordan1': 'air jordan 1', 'jordan1': 'jordan 1',
    'newbalance': 'new balance', 'oncloud': 'on cloud', 'wayofwade': 'way of wade',
    'vapormax': 'vapor max', 'zoomfly': 'zoom fly', 'pegasusplus': 'pegasus plus',
    'dunklow': 'dunk low', 'dunkhigh': 'dunk high', 'sambaog': 'samba og',
    'p6000': 'p 6000', 'ld1000': 'ld 1000',
    // ---- abbreviations / nicknames (verified better than raw, or raw=0) ----
    'af1': 'air force 1', 'afone': 'air force 1', 'forces': 'air force',
    'am1': 'air max 1', 'am90': 'air max 90', 'am95': 'air max 95',
    'am97': 'air max 97', 'am270': 'air max 270', 'amplus': 'air max plus',
    'tns': 'air max plus', 'tn': 'air max plus',
    'wow': 'way of wade', 'woc': 'all city',
    'nb': 'new balance', 'yzy': 'yeezy', 'j1': 'jordan 1', 'aj1': 'air jordan 1',
    'ub': 'ultraboost', 'sl72': 'sl 72', 'spzl': 'spezial',
    'cm': 'cloudmonster', 'sf': 'superfly',
    // ---- athlete / signature lines ----
    'kobe': 'kobe', 'mamba': 'kobe', 'lebron': 'lebron', 'bron': 'lebron',
    'kd': 'kd', 'durant': 'kd',                 // keep 'kd' (already good); map 'durant'->kd
    'sabrina': 'sabrina', 'ja': 'ja', 'gt': 'gt',
    'messi': 'messi', 'kylian': 'kylian', 'mbappe': 'kylian', 'wade': 'wade',
    // ---- common misspellings ----
    'jordn': 'jordan', 'jordon': 'jordan', 'jrodan': 'jordan',
    'addidas': 'adidas', 'adiddas': 'adidas', 'adias': 'adidas',
    'nikee': 'nike', 'nkie': 'nike', 'gazele': 'gazelle', 'gazel': 'gazelle',
    'samaba': 'samba', 'superstart': 'superstar', 'vomeo': 'vomero',
    'pegassus': 'pegasus', 'pegaus': 'pegasus', 'mercurail': 'mercurial',
    'ultrabost': 'ultraboost', 'ultra boost': 'ultraboost',
    // ---- plurals / stems ----
    'jordans': 'jordan', 'sambas': 'samba', 'gazelles': 'gazelle', 'dunks': 'dunk',
    'superstars': 'superstar', 'campuses': 'campus', 'cortezs': 'cortez',
    'predators': 'predator', 'cliftons': 'clifton', 'mercurials': 'mercurial'
  };

  // Words to drop (everything here is a shoe; these only hurt AND-search).
  var FILLER = ['shoes', 'shoe', 'sneakers', 'sneaker', 'trainers', 'trainer',
    'footwear', 'kicks', 'pair', 'pairs', 'the', 'a', 'for'];

  var FILLER_SET = {};
  FILLER.forEach(function (w) { FILLER_SET[w] = true; });

  // Build a lookup of compound "nospace" -> "spaced".
  var COMPOUND_NOSPACE = {};
  COMPOUNDS.forEach(function (c) { COMPOUND_NOSPACE[c.replace(/\s+/g, '')] = c; });

  function rewrite(raw) {
    if (!raw) return raw;
    var q = String(raw).toLowerCase().trim().replace(/\s+/g, ' ');
    if (!q) return raw;

    // 1) whole-query alias (e.g. "kd" -> "kevin durant")
    if (ALIASES[q]) q = ALIASES[q];
    // 1b) whole-query compound nospace (e.g. "airforce" -> "air force")
    else if (COMPOUND_NOSPACE[q]) q = COMPOUND_NOSPACE[q];

    // 2) token-level pass
    var tokens = q.split(' ');
    var out = [];
    for (var i = 0; i < tokens.length; i++) {
      var tk = tokens[i];
      if (!tk) continue;
      if (FILLER_SET[tk]) continue;                 // drop filler
      if (ALIASES[tk]) { out.push(ALIASES[tk]); continue; }       // nickname
      if (COMPOUND_NOSPACE[tk]) { out.push(COMPOUND_NOSPACE[tk]); continue; } // compound nospace
      out.push(tk);
    }
    var result = out.join(' ').replace(/\s+/g, ' ').trim();

    // 3) safety: never return empty (e.g. user typed only "shoes")
    if (!result) return raw;
    return result;
  }

  // Expose for testing
  window.__orjnRewriteQuery = rewrite;

  /* --- INSTANT / PREDICTIVE dropdown ---
     The predictive component fetches /search/suggest (predictive_search_url) with
     ?q=<typed text> as you type. We intercept fetch() and rewrite ONLY the q param
     for those predictive requests, so the dropdown suggestions match (airforce ->
     air force) — WITHOUT changing the text the user sees in the box. */
  function isPredictiveUrl(u) {
    try {
      var s = String(u);
      return /\/search\/suggest/i.test(s) || /[?&]section_id=predictive-search/i.test(s) ||
             (/[?&]q=/.test(s) && /predictive/i.test(s));
    } catch (e) { return false; }
  }
  function rewriteUrlQ(urlStr) {
    try {
      var url = new URL(urlStr, location.origin);
      var q = url.searchParams.get('q');
      if (!q) return urlStr;
      // don't touch id-based lookups (recently viewed etc.)
      if (/(^|\s)id:/i.test(q)) return urlStr;
      var nq = rewrite(q);
      if (nq && nq !== q) {
        url.searchParams.set('q', nq);
        return url.toString();
      }
    } catch (e) {}
    return urlStr;
  }
  if (typeof window.fetch === 'function' && !window.__orjnFetchPatched) {
    window.__orjnFetchPatched = true;
    var _fetch = window.fetch;
    window.fetch = function (input, init) {
      try {
        if (typeof input === 'string' && isPredictiveUrl(input)) {
          input = rewriteUrlQ(input);
        } else if (input && input.url && isPredictiveUrl(input.url)) {
          // Request object: rebuild with rewritten URL
          var newUrl = rewriteUrlQ(input.url);
          if (newUrl !== input.url) input = new Request(newUrl, input);
        }
      } catch (e) {}
      return _fetch.call(this, input, init);
    };
  }

  // --- Apply on the SEARCH RESULTS page: if q can be improved, replace it ---
  function rewriteResultsUrlOnce() {
    if (!/\/search/.test(location.pathname)) return;
    var params = new URLSearchParams(location.search);
    var q = params.get('q');
    if (!q) return;
    if (params.get('orjn_rw') === '1') return; // already rewritten, avoid loops
    var nq = rewrite(q);
    if (nq && nq !== q.toLowerCase().trim()) {
      params.set('q', nq);
      params.set('orjn_rw', '1');
      location.replace(location.pathname + '?' + params.toString());
    }
  }

  // --- Apply on SUBMIT from any search form (so the typed query is rewritten) ---
  function hookForms() {
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!form.querySelector('input[name="q"]')) return;
      var input = form.querySelector('input[name="q"]');
      var nq = rewrite(input.value);
      if (nq && nq !== input.value.toLowerCase().trim()) {
        input.value = nq;
      }
    }, true);
  }

  /* --- Predictive dropdown for the SEARCH-RESULTS-PAGE input ---
     That input (search-page-input-component) is a plain box with no suggestions,
     unlike the header search. We attach a lightweight dropdown that fetches the
     same predictive section the header uses (/search/suggest?...&section_id=
     predictive-search) — which returns ready-made HTML — and shows it under the
     input as the user types. The fetch interceptor above rewrites the query, so
     it's smart too. */
  function attachSearchPageDropdown() {
    var comp = document.querySelector('search-page-input-component');
    if (!comp || comp.dataset.orjnDropdown) return;
    var input = comp.querySelector('input[name="q"]');
    if (!input) return;
    comp.dataset.orjnDropdown = '1';

    // dropdown container
    var box = document.createElement('div');
    box.className = 'orjn-search-suggest';
    box.style.cssText = 'position:absolute;left:0;right:0;top:100%;z-index:60;background:#0a0a0a;border:1px solid rgba(245,245,245,0.14);box-shadow:0 22px 60px rgba(0,0,0,0.5);max-height:70vh;overflow:auto;display:none;';
    // anchor: make the component position:relative so the box sits under the input
    if (getComputedStyle(comp).position === 'static') comp.style.position = 'relative';
    comp.appendChild(box);

    var ctrl = null, t = null, lastQ = '';

    function close() { box.style.display = 'none'; box.innerHTML = ''; }
    function open() { if (box.innerHTML.trim()) box.style.display = 'block'; }

    function render(html) {
      // The suggest section returns a full predictive-search section; pull its
      // results wrapper so styling stays consistent with the theme.
      try {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var results = doc.querySelector('#predictive-search-results, .predictive-search-results, [data-predictive-results]')
                   || doc.querySelector('.predictive-search-form__content-wrapper')
                   || doc.body;
        box.innerHTML = results ? results.innerHTML : '';
      } catch (e) { box.innerHTML = html; }
      // fix relative product links keep working (they already are absolute/relative)
      open();
    }

    function fetchSuggest(q) {
      var url = '/search/suggest?q=' + encodeURIComponent(q) +
                '&section_id=predictive-search' +
                '&resources[limit]=6&resources[limit_scope]=each';
      if (ctrl) { try { ctrl.abort(); } catch (e) {} }
      ctrl = (typeof AbortController !== 'undefined') ? new AbortController() : null;
      fetch(url, { signal: ctrl ? ctrl.signal : undefined, headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function (r) { return r.ok ? r.text() : ''; })
        .then(function (html) { if (html) render(html); else close(); })
        .catch(function () {});
    }

    input.addEventListener('input', function () {
      var q = input.value.trim();
      if (q === lastQ) return;
      lastQ = q;
      clearTimeout(t);
      if (q.length < 2) { close(); return; }
      t = setTimeout(function () { fetchSuggest(q); }, 200);
    });
    input.addEventListener('focus', function () { if (input.value.trim().length >= 2 && box.innerHTML.trim()) open(); });
    document.addEventListener('click', function (e) {
      if (!comp.contains(e.target)) close();
    }, true);
    input.addEventListener('keydown', function (e) { if (e.key === 'Escape') close(); });
  }

  function init() {
    hookForms();
    rewriteResultsUrlOnce();
    attachSearchPageDropdown();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', attachSearchPageDropdown);
})();
